import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon
} from '@mui/icons-material';
import { apiRequest } from '../utils/api';

const FieldMappings = () => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [currentMapping, setCurrentMapping] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    isDefault: false,
    dateFormat: 'YYYY-MM-DD',
    numberFormat: 'en-US',
    delimiter: ',',
    hasHeader: true,
    mappings: {
      date: '',
      symbol: '',
      quantity: '',
      price: '',
      fees: '',
      amount: '',
      action: '',
      externalId: '',
      description: ''
    }
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Required fields for mapping (price is optional for some formats like Tradier)
  const requiredFields = ['date', 'symbol', 'quantity', 'action'];
  
  // CSV field options (common headers found in broker exports)
  const csvFieldOptions = [
    'Date', 'Trade Date', 'Settlement Date', 'Transaction Date', 'Close Date',
    'Symbol', 'Ticker', 'Security', 'Underlying',
    'Action', 'Type', 'Transaction Type', 'Activity Type',
    'Quantity', 'Qty', 'Amount', 'Shares',
    'Price', 'Trade Price', 'Execution Price',
    'Cost', 'Proceeds', 'G/L($)', 'P/L', 'Realized P/L',
    'Commission', 'Fees', 'Fee', 'Total Fees',
    'Net Amount', 'Net', 'Total',
    'Order ID', 'Transaction ID', 'Confirmation', 'Reference',
    'Description', 'Memo', 'Notes', 'Activity Description'
  ];

  // Fetch field mappings
  useEffect(() => {
    fetchMappings();
  }, []);

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const response = await apiRequest('/api/field-mappings');
      setMappings(response.data);
    } catch (error) {
      console.error('Error fetching mappings:', error);
      setError('Failed to load field mappings');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (mapping = null) => {
    if (mapping) {
      // Edit existing mapping
      setCurrentMapping(mapping);
      setFormData({
        name: mapping.name,
        isDefault: mapping.isDefault,
        dateFormat: mapping.dateFormat || 'YYYY-MM-DD',
        numberFormat: mapping.numberFormat || 'en-US',
        delimiter: mapping.delimiter || ',',
        hasHeader: mapping.hasHeader !== false,
        mappings: { ...mapping.mappings }
      });
    } else {
      // Create new mapping
      setCurrentMapping(null);
      setFormData({
        name: '',
        isDefault: false,
        dateFormat: 'YYYY-MM-DD',
        numberFormat: 'en-US',
        delimiter: ',',
        hasHeader: true,
        mappings: {
          date: '',
          symbol: '',
          quantity: '',
          price: '',
          fees: '',
          amount: '',
          action: '',
          externalId: '',
          description: ''
        }
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDeleteDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleMappingChange = (field, value) => {
    setFormData({
      ...formData,
      mappings: {
        ...formData.mappings,
        [field]: value
      }
    });
  };

  const handleSubmit = async () => {
    // Validate form
    const missingFields = requiredFields.filter(field => !formData.mappings[field]);
    if (!formData.name) {
      setError('Mapping name is required');
      return;
    }
    if (missingFields.length > 0) {
      setError(`Required fields missing: ${missingFields.join(', ')}`);
      return;
    }

    setLoading(true);
    try {
      if (currentMapping) {
        // Update existing mapping
        await apiRequest(`/api/field-mappings/${currentMapping.id}`, {
        method: 'PUT',
        body: formData
      });
        setSuccess(`Mapping "${formData.name}" updated successfully`);
      } else {
        // Create new mapping
        await apiRequest('/api/field-mappings', {
        method: 'POST',
        body: formData
      });
        setSuccess(`Mapping "${formData.name}" created successfully`);
      }
      handleCloseDialog();
      fetchMappings();
    } catch (error) {
      console.error('Error saving mapping:', error);
      setError(error.response?.data?.message || 'Failed to save mapping');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!currentMapping) return;

    setLoading(true);
    try {
      await apiRequest(`/api/field-mappings/${currentMapping.id}`, {
      method: 'DELETE'
    });
      setSuccess(`Mapping "${currentMapping.name}" deleted successfully`);
      handleCloseDialog();
      fetchMappings();
    } catch (error) {
      console.error('Error deleting mapping:', error);
      setError(error.response?.data?.message || 'Failed to delete mapping');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (mapping) => {
    setLoading(true);
    try {
      await apiRequest(`/api/field-mappings/${mapping.id}/default`, {
      method: 'PUT'
    });
      setSuccess(`"${mapping.name}" set as default mapping`);
      fetchMappings();
    } catch (error) {
      console.error('Error setting default mapping:', error);
      setError('Failed to set default mapping');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteDialog = (mapping) => {
    setCurrentMapping(mapping);
    setDeleteDialog(true);
  };

  const handleCloseAlert = () => {
    setError(null);
    setSuccess(null);
  };

  const createTradierMapping = async () => {
    setLoading(true);
    try {
      const response = await apiRequest('/api/field-mappings/create-tradier', {
      method: 'POST'
    });
      setSuccess('Tradier field mapping created successfully');
      fetchMappings();
    } catch (error) {
      console.error('Error creating Tradier mapping:', error);
      setError(error.response?.data?.message || 'Failed to create Tradier mapping');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body1">
          Create and manage field mappings for CSV imports
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={createTradierMapping}
            disabled={loading}
          >
            Add Tradier Format
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            New Mapping
          </Button>
        </Box>
      </Box>

      {loading && !openDialog ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : mappings.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" align="center">
              No field mappings found. Create your first mapping to import CSV files.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Default</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Date Format</TableCell>
                <TableCell>Required Fields</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappings.map((mapping) => (
                <TableRow key={mapping.id}>
                  <TableCell>
                    {mapping.isDefault ? (
                      <StarIcon color="primary" />
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => handleSetDefault(mapping)}
                        title="Set as default"
                      >
                        <StarIcon color="disabled" />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>{mapping.name}</TableCell>
                  <TableCell>{mapping.dateFormat || 'YYYY-MM-DD'}</TableCell>
                  <TableCell>
                    {Object.entries(mapping.mappings)
                      .filter(([key, value]) => requiredFields.includes(key) && value)
                      .map(([key, value]) => (
                        <Typography key={key} variant="body2" component="span" sx={{ mr: 1 }}>
                          {key}: <strong>{value}</strong>
                        </Typography>
                      ))}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(mapping)}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    {!mapping.isDefault && (
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(mapping)}
                        title="Delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {currentMapping ? `Edit Mapping: ${currentMapping.name}` : 'Create New Mapping'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 0 }}>
            {/* Basic Settings */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mapping Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                margin="normal"
                required
              />
              
              <FormGroup sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isDefault}
                      onChange={handleSwitchChange}
                      name="isDefault"
                    />
                  }
                  label="Set as default mapping"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.hasHeader}
                      onChange={handleSwitchChange}
                      name="hasHeader"
                    />
                  }
                  label="CSV has header row"
                />
              </FormGroup>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date Format"
                name="dateFormat"
                value={formData.dateFormat}
                onChange={handleInputChange}
                margin="normal"
                helperText="e.g., YYYY-MM-DD, MM/DD/YYYY"
              />
              
              <TextField
                fullWidth
                label="CSV Delimiter"
                name="delimiter"
                value={formData.delimiter}
                onChange={handleInputChange}
                margin="normal"
                helperText="e.g., , (comma), ; (semicolon), tab"
              />
              
              <TextField
                fullWidth
                label="Number Format"
                name="numberFormat"
                value={formData.numberFormat}
                onChange={handleInputChange}
                margin="normal"
                helperText="e.g., en-US, de-DE"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Field Mappings
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Map your CSV columns to the application fields. Fields marked with * are required.
              </Typography>
            </Grid>
            
            {/* Field Mappings */}
            {Object.entries(formData.mappings).map(([field, value]) => (
              <Grid item xs={12} sm={6} md={4} key={field}>
                <FormControl fullWidth sx={{ minWidth: 200 }}>
                  <InputLabel>
                    {field.charAt(0).toUpperCase() + field.slice(1)}
                    {requiredFields.includes(field) && ' *'}
                  </InputLabel>
                  <Select
                    value={value}
                    onChange={(e) => handleMappingChange(field, e.target.value)}
                    label={`${field.charAt(0).toUpperCase() + field.slice(1)}${requiredFields.includes(field) ? ' *' : ''}`}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 300, width: 'auto', minWidth: 200 } } }}
                  >
                    <MenuItem value="">Not Mapped</MenuItem>
                    {csvFieldOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the mapping "{currentMapping?.name}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FieldMappings;